import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

export default function Kontakt() {
  return (
    <>
      <SEO
        title="Kontakt - Akut Vikaren ApS"
        description="Kontakt Akut Vikaren ApS. Uanset om I står i en akut situation, eller I er ved at planlægge en kommende indsats, er I velkomne til at kontakte os. Vi dækker hele Jylland og Fyn samt hele landet ved akut anbringelse."
        ogImage="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00009.webp"
      />

      {/* Hero Header */}
      <section className="relative bg-[#041c2c] text-white py-16 sm:py-24 border-b border-slate-800 overflow-hidden">
        {/* Background image with dark transparent overlay */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00009.webp"
            alt="Kontakt Akutvikaren – professionel rådgivning og samtale"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 45%' }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#041c2c]/90 via-[#041c2c]/75 to-[#041c2c]/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700/80 text-xs font-semibold text-[#b39366] uppercase tracking-wider">
              <i className="fa-solid fa-envelope-open-text text-xs" aria-hidden="true"></i>
              <span>Døgnåben kontakt</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Kontakt
            </h1>
            <p className="text-slate-300 text-lg sm:text-xl font-normal leading-relaxed">
              Har I brug for hjælp – nu eller på sigt?
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact info & Directions */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Information</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#041c2c] tracking-tight">
                  Akut Vikaren ApS
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  Uanset om I står i en akut situation, eller I er ved at planlægge en kommende indsats, er I velkomne til at kontakte os. Vi dækker hele Jylland og Fyn.
                </p>
                <p className="text-slate-600 text-sm">
                  Ring til os, eller udfyld kontaktformularen – vi vender tilbage hurtigst muligt.
                </p>
              </div>

              {/* Contact Cards Grid */}
              <div className="space-y-4">
                {/* Telefon */}
                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-5 flex items-start gap-4 shadow-xs">
                  <div className="w-11 h-11 rounded-lg bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-lg shrink-0 mt-0.5">
                    <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Telefon</h3>
                    <a
                      href="tel:+4523964042"
                      className="text-lg font-bold text-[#041c2c] hover:text-[#b39366] transition-colors block mt-0.5 whitespace-nowrap"
                    >
                      (+45) 23 96 40 42
                    </a>
                    <p className="text-xs text-slate-500 mt-1">Døgnåben tilgængelighed på opkald og SMS.</p>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-5 flex items-start gap-4 shadow-xs">
                  <div className="w-11 h-11 rounded-lg bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-lg shrink-0 mt-0.5">
                    <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</h3>
                    <a
                      href="mailto:kontakt@akutvikaren.dk"
                      className="text-base font-semibold text-[#041c2c] hover:text-[#b39366] transition-colors block mt-0.5 whitespace-nowrap"
                    >
                      kontakt@akutvikaren.dk
                    </a>
                    <p className="text-xs text-slate-500 mt-1">Vi besvarer din henvendelse hurtigst muligt.</p>
                  </div>
                </div>

                {/* Adresse & Find Vej */}
                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-5 flex items-start gap-4 shadow-xs">
                  <div className="w-11 h-11 rounded-lg bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-lg shrink-0 mt-0.5">
                    <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                  </div>
                  <div className="space-y-2 flex-1">
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Adresse</h3>
                      <p className="text-base font-semibold text-[#041c2c] mt-0.5">
                        Alphavej 17, 8270 Højbjerg
                      </p>
                    </div>

                    <a
                      href="https://maps.app.goo.gl/CGnJQRshRRNBUviN6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-white border border-[#eae6dd] text-[#b39366] hover:text-[#041c2c] hover:border-[#b39366] px-3.5 py-2 rounded text-xs font-bold transition-all shadow-xs"
                    >
                      <i className="fa-solid fa-map-location-dot" aria-hidden="true"></i>
                      <span>Find vej på Google Maps</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true"></i>
                    </a>
                  </div>
                </div>

                {/* Vikar / Ansøger Callout */}
                <div className="bg-[#041c2c] text-white rounded-xl p-5 shadow-sm border border-slate-800 space-y-3">
                  <div className="flex items-center gap-2 text-[#b39366] text-xs font-bold uppercase tracking-wider">
                    <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
                    <span>Er du vikar eller ansøger?</span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-200 leading-relaxed font-medium">
                    Vi ansætter social og sundheds faglige uddannet medarbejder eller personer med minimum tre års relevant erhvervs erfaring ..
                  </p>
                  <Link
                    to="/bliv-vikar"
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#b39366] hover:text-white transition-colors pt-1"
                  >
                    <span>Læs mere om faggrupper og søg vikarjob</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                  </Link>
                </div>

                {/* Sociale Medier */}
                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-5 shadow-xs">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Følg os på sociale medier</h3>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.facebook.com/Akutvikaren"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded bg-white border border-[#eae6dd] text-[#041c2c] hover:text-[#b39366] hover:border-[#b39366] text-xs font-semibold transition-all shadow-xs"
                    >
                      <i className="fa-brands fa-facebook-f text-sm text-[#b39366]" aria-hidden="true"></i>
                      <span>Facebook</span>
                    </a>
                    <a
                      href="https://www.instagram.com/akutvikaren"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded bg-white border border-[#eae6dd] text-[#041c2c] hover:text-[#b39366] hover:border-[#b39366] text-xs font-semibold transition-all shadow-xs"
                    >
                      <i className="fa-brands fa-instagram text-sm text-[#b39366]" aria-hidden="true"></i>
                      <span>Instagram</span>
                    </a>
                    <a
                      href="https://www.linkedin.com/company/akut-vikaren-aps/?originalSubdomain=dk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded bg-white border border-[#eae6dd] text-[#041c2c] hover:text-[#b39366] hover:border-[#b39366] text-xs font-semibold transition-all shadow-xs"
                    >
                      <i className="fa-brands fa-linkedin-in text-sm text-[#b39366]" aria-hidden="true"></i>
                      <span>LinkedIn</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <div className="p-0">
                <ContactForm formTitle="Kontaktformular (Kontaktside)" />
              </div>
            </div>
          </div>

          {/* Google Maps Interactive Embed - Full width matching the grid container */}
          <div className="mt-12 sm:mt-16 group rounded-2xl overflow-hidden border border-[#eae6dd] shadow-xs bg-slate-100 relative transition-all duration-300 hover:border-[#b39366]/60 hover:shadow-md h-[200px] lg:h-[350px] w-full">
            <iframe
              title="Kort over Akut Vikaren ApS, Alphavej 17, 8270 Højbjerg"
              src="https://maps.google.com/maps?q=Alphavej%2017,%208270%20H%C3%B8jbjerg&t=&z=14&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full border-0 grayscale-[85%] contrast-[110%] transition-all duration-500 ease-in-out group-hover:grayscale-0 group-hover:contrast-100"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </>
  );
}
